import React, { useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type {
	ObjectListFlyoutHookParams,
	ObjectListFlyoutHookResults,
	ObjectListFlyoutItem,
} from '@atlassian/navigation-common/types';

import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { formatTimeAgo } from '@confluence/time-ago';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { PageStarButton } from '@confluence/action-buttons/entry-points/pageStar.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { starredContentMenuQueryVars } from '../../ContentMenu/starredContentMenuConstants.ts';
import StarredContentMenuQuery, {
	type StarredContentMenuQuery$data,
	type StarredContentMenuQuery$variables,
} from '../../ContentMenu/__generated__/StarredContentMenuQuery.graphql.ts';
import { fireContentMenuItemClickedAnalytics } from '../../ContentMenu/analyticsCallbacks.ts';

import { getContentEmojiAndTitle, i18n as titleMessages } from './getContentEmojiandTitle.ts';

export type UseStarredDataParams = {
	searchText?: string;
};

export const useStarredItems = (
	params: ObjectListFlyoutHookParams,
): ObjectListFlyoutHookResults<ObjectListFlyoutItem> => {
	const intl = useIntl();

	const { data, loading, error, filteredNodes, hasNextPage, loadMore } = useStarredData({
		searchText: params.searchText,
	});

	const sections = useMemo(() => {
		if (error) {
			return [];
		}

		const items: ObjectListFlyoutItem[] = [];
		for (const node of filteredNodes) {
			if (!node) {
				continue;
			}
			const { emoji, title } = getContentEmojiAndTitle(node);

			items.push({
				elemBefore: node.type ? (
					<ContentIcon type={node.type} subType={node.subType} emoji={emoji} />
				) : null,
				elemAfter: node.id ? (
					<StarButton
						contentId={node.id}
						contentType={node.type}
						isStarred={node.metadata?.currentuser?.favourited?.isFavourite || false}
					/>
				) : undefined,
				primaryLabel: title || intl.formatMessage(titleMessages.untitled),
				secondaryLabel: node.space?.name || '',
				tertiaryLabel: node.metadata?.currentuser?.favourited?.favouritedDate
					? formatTimeAgo(intl, node.metadata.currentuser.favourited.favouritedDate)
					: undefined,
				href: `${CONTEXT_PATH}${node.links?.webui || ''}`,
				extendedSubItemAnalyticsAttributes: {
					componentType: 'customItem',
					contentType: node.type ?? null,
					contentId: node.id ?? null,
					contentStatus: node.status ?? null,
					isLivePage: node.subType === 'live',
				},
			});
		}

		return items.length > 0 ? [{ header: '', items }] : [];
	}, [filteredNodes, error, intl]);

	return {
		sections,
		hasMore: hasNextPage,
		isLoading: loading && !data,
		loadMore,
		error: error || undefined,
	};
};

export const useStarredData = (params: UseStarredDataParams) => {
	const searchText = params.searchText || '';
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { data, loading, error, fetchMore, networkStatus } = useQueryInterop<
		StarredContentMenuQuery$data,
		StarredContentMenuQuery$variables
	>(StarredContentMenuQuery, {
		variables: starredContentMenuQueryVars,
		fetchPolicy: 'cache-and-network',
	});

	const hasNextPage = data?.favoriteContent?.pageInfo?.hasNextPage || false;

	const filteredNodes = useMemo(() => {
		if (error) {
			return [];
		}

		const lowerFilterText = searchText.toLowerCase();

		return (
			uniqBy(
				data?.favoriteContent?.nodes?.filter(
					(content): content is NonNullable<typeof data.favoriteContent.nodes>[number] =>
						content !== null &&
						content !== undefined &&
						(!searchText || (content.title?.toLowerCase() || '').includes(lowerFilterText)),
				),
				(content) => content?.id,
			) ?? []
		);
	}, [data, error, searchText]);

	const loadMore = useCallback(() => {
		if (networkStatus === NetworkStatus.ready && hasNextPage) {
			fetchMoreStarredContent(fetchMore, data?.favoriteContent?.nodes?.length || 0);
		}
	}, [networkStatus, hasNextPage, fetchMore, data?.favoriteContent?.nodes]);

	if (error) {
		experienceTracker.stopOnError({
			name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
			error,
			attributes: { navVersion: '4', dropdownType: 'starred' },
		});
	}

	return {
		data,
		loading,
		error,
		filteredNodes,
		hasNextPage,
		loadMore,
	};
};

export const fetchMoreStarredContent = (
	fetchMore: (options: {
		variables: { start?: number };
		updateQuery: (
			prev: StarredContentMenuQuery$data,
			options: { fetchMoreResult?: StarredContentMenuQuery$data },
		) => StarredContentMenuQuery$data;
	}) => void,
	start: number,
) => {
	void fetchMore({
		variables: {
			start,
		},
		updateQuery: (prev, { fetchMoreResult }) => {
			if (!fetchMoreResult) {
				return prev;
			}
			const prevEdges = prev.favoriteContent?.nodes || [];
			const fetchMoreEdges = fetchMoreResult.favoriteContent?.nodes || [];
			const mergedEdges = [...prevEdges, ...fetchMoreEdges];

			const result = { ...fetchMoreResult };
			set(result, 'favoriteContent.nodes', mergedEdges);
			return result;
		},
	});
};

const StarButton = ({
	contentId,
	contentType,
	isStarred,
}: {
	contentId: string;
	contentType: string | null | undefined;
	isStarred: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onStar = useCallback(() => {
		fireContentMenuItemClickedAnalytics(createAnalyticsEvent, 'starredNavigationMenuItem', {
			componentType: 'customItemAction',
			itemId: 'star',
			parentContentType: contentType,
			parentContentId: contentId,
		})();
	}, [createAnalyticsEvent, contentId, contentType]);

	const onUnstar = useCallback(() => {
		fireContentMenuItemClickedAnalytics(createAnalyticsEvent, 'starredNavigationMenuItem', {
			componentType: 'customItemAction',
			itemId: 'unstar',
			parentContentType: contentType,
			parentContentId: contentId,
		})();
	}, [createAnalyticsEvent, contentId, contentType]);

	return (
		<PageStarButton
			contentId={contentId}
			isStarred={isStarred}
			size="small"
			tooltipPosition="top"
			onStar={onStar}
			onUnstar={onUnstar}
			spacing="none"
		/>
	);
};
